'use client';

import { Card, DatePicker, Select, SelectOption } from '@repo/ui';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';

import { dateUtil } from '@xtnd/web/shared/utils';
import { xtndColors } from '@repo/tailwind-config';

export default function Performance() {
  const [searchParams, setSearchParams] = useSearchParams();

  const TEAM_MEMBER_OPTIONS = [
    { value: 'all', label: 'All team members' },
    { value: 'team-member-1', label: 'Team Member 1' },
    { value: 'team-member-2', label: 'Team Member 2' },
    { value: 'team-member-3', label: 'Team Member 3' },
  ];

  const [teamMember, setTeamMember] = useState<SelectOption>(() => {
    const teamMemberParam = searchParams.get('teamMember');
    const teamMemberOption = TEAM_MEMBER_OPTIONS.find(option => option.value === teamMemberParam);
    return (
      teamMemberOption ?? {
        value: 'all',
        label: 'All team members',
      }
    );
  });

  // Default to current month (first day to last day of current month)
  const getCurrentMonthRange = () => {
    const now = new Date();
    const startDate = new Date(now.getFullYear(), now.getMonth(), 1);
    const endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { startDate, endDate };
  };

  const defaultDateRange = getCurrentMonthRange();

  const [dateRange, setDateRange] = useState<{ startDate: Date | null; endDate: Date | null }>(
    () => {
      const startDateParam = searchParams.get('startDate');
      const endDateParam = searchParams.get('endDate');
      return {
        startDate: startDateParam ? new Date(startDateParam) : defaultDateRange.startDate,
        endDate: endDateParam ? new Date(endDateParam) : defaultDateRange.endDate,
      };
    }
  );

  useEffect(() => {
    let query = {};
    if (dateRange.startDate && dateRange.endDate) {
      query = {
        startDate: dateUtil.formatDate(dateRange.startDate, dateUtil.DateFormat.US),
        endDate: dateUtil.formatDate(dateRange.endDate, dateUtil.DateFormat.US),
        tab: 'performance',
      };
    }

    if (teamMember) {
      query = {
        ...query,
        teamMember: teamMember.value,
        tab: 'performance',
      };
    }

    setSearchParams(query);
  }, [dateRange, teamMember, setSearchParams]);

  const contactsPerDay = [
    { date: 'Nov 24', contacts: 0 },
    { date: 'Nov 25', contacts: 2 },
    { date: 'Nov 26', contacts: 0 },
    { date: 'Nov 27', contacts: 0 },
    { date: 'Nov 28', contacts: 0 },
    { date: 'Nov 29', contacts: 0 },
    { date: 'Nov 30', contacts: 0 },
  ];

  return (
    <section>
      <Card>
        {/* HEADER */}
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">Filters</h3>
        </div>
        <div className="flex items-center gap-8 mt-4">
          <div className="flex flex-col  gap-y-2">
            <div className="text-sm text-xtnd-dark dark:text-xtnd-light">
              <p>AR Team Member</p>
            </div>
            <div>
              <Select
                options={TEAM_MEMBER_OPTIONS}
                value={teamMember}
                className="text-sm"
                onChange={value => {
                  setTeamMember(value as SelectOption);
                }}
              />
            </div>
          </div>
          <div className="flex flex-col  gap-y-2">
            <div className="text-sm text-xtnd-dark dark:text-xtnd-light">
              <p>Date Range</p>
            </div>
            <div>
              <DatePicker
                id="dateRange"
                mode="range"
                className="text-sm"
                value={dateRange}
                onChange={range => {
                  // Only update when both dates are selected
                  if (range.startDate && range.endDate) {
                    setDateRange({
                      startDate: range.startDate,
                      endDate: range.endDate,
                    });
                  }
                }}
                format={dateUtil.DateFormat.US}
                required
              />
            </div>
          </div>
        </div>
      </Card>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <Card>
          <div className="flex flex-col ">
            <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
              Total Contacts
            </h3>
            <span className="text-3xl font-bold text-xtnd-dark dark:text-xtnd-light pt-3">0</span>
          </div>
        </Card>
        <Card>
          <div className="flex flex-col">
            <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
              Avg Contacts/Day
            </h3>
            <span className="text-3xl font-bold text-xtnd-dark dark:text-xtnd-light pt-3">0.0</span>
          </div>
        </Card>
      </div>
      <Card className="mt-4">
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold text-xtnd-dark dark:text-xtnd-light">
            Contacts Per Day
          </h3>
        </div>
        <div className="w-full h-96">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={contactsPerDay} margin={{ top: 20, right: 30, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fill: '#6b7280', fontSize: 12 }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
                dy={10}
              />
              <YAxis
                tick={{ fill: '#6b7280', fontSize: 12 }}
                axisLine={{ stroke: '#e5e7eb' }}
                tickLine={false}
                allowDecimals={false}
                domain={[0, 4]}
                ticks={[1, 2, 3, 4]}
                dx={-10}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px',
                  fontSize: '12px',
                }}
                cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
              />
              <Bar
                dataKey="contacts"
                fill={xtndColors?.['xtnd-blue']?.DEFAULT}
                radius={[4, 4, 0, 0]}
                maxBarSize={40}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </section>
  );
}
